import { startCase } from "lodash";
import { t } from "@/i18n-js/instance";
import { Typography } from "@circle-react-shared/uikit/Typography";
import {
  formatValueDynamically,
  getFormattedValue,
} from "../../../../utils/previewValueFormatterUtils";
import { PreviewCardLayout } from "./PreviewCardLayout";

type MemberTagParams = {
  name?: string;
  emoji?: string;
  color?: string;
  is_public?: boolean;
  display_locations?: string;
  display_format?: string;
} & Record<string, string | number | boolean | undefined>;

type MemberTagFieldKey =
  | "name"
  | "emoji"
  | "color"
  | "is_public"
  | "display_locations"
  | "display_format";

interface MemberTagFieldConfig {
  defaultValue?: string | boolean;
  label: string;
  type: "string" | "boolean";
}

interface MemberTagCardProps {
  params: MemberTagParams;
  url: string;
}

export const MemberTagCard = ({ params, url }: MemberTagCardProps) => {
  const MEMBER_TAG_FIELDS: Array<[MemberTagFieldKey, MemberTagFieldConfig]> = [
    [
      "name",
      {
        label: t("member_tags.form.name", { defaultValue: "Name" }),
        type: "string",
      },
    ],
    [
      "emoji",
      {
        label: t("member_tags.form.icon", { defaultValue: "Icon" }),
        type: "string",
      },
    ],
    [
      "color",
      {
        label: t("member_tags.form.color", { defaultValue: "Color" }),
        type: "string",
      },
    ],
    [
      "is_public",
      {
        label: t("member_tags.visible", { defaultValue: "Public" }),
        type: "boolean",
      },
    ],
    [
      "display_locations",
      {
        label: t("member_tags.form.display_locations", {
          defaultValue: "Display locations",
        }),
        type: "string",
      },
    ],
    [
      "display_format",
      {
        label: t("member_tags.form.display_format", {
          defaultValue: "Display format",
        }),
        type: "string",
      },
    ],
  ];

  return (
    <PreviewCardLayout
      title={t(
        "help_widget.copilot.preview_cards.create_member_tag_card_title",
      )}
      url={url}
    >
      <ul className="flex flex-col leading-5">
        {MEMBER_TAG_FIELDS.flatMap(([key, keyProps]) => {
          const hasValueInParams = key in params && params[key] != null;
          const value = hasValueInParams ? params[key] : keyProps.defaultValue;

          if (value == null) return [];

          return [
            <li key={key} className="text-darkest !pb-0">
              <Typography.BodySm color="text-darkest">
                <strong>{keyProps.label}:</strong>{" "}
                {getFormattedValue(value, keyProps.type)}
              </Typography.BodySm>
            </li>,
          ];
        })}
        {Object.entries(params)
          .filter(
            ([key, value]) =>
              !MEMBER_TAG_FIELDS.find(([fieldKey]) => fieldKey === key) &&
              value != null,
          )
          .flatMap(([key, value]) => {
            if (value == null) return [];

            const displayValue = value as string | number | boolean | string[];

            return [
              <li key={key} className="text-darkest !pb-0">
                <Typography.BodySm color="text-darkest">
                  <strong>{startCase(key)}:</strong>{" "}
                  {formatValueDynamically(displayValue)}
                </Typography.BodySm>
              </li>,
            ];
          })}
      </ul>
    </PreviewCardLayout>
  );
};
